import { Fragment, useState } from "react";
import {
	Box,
	CircularProgress,
	Grid2,
	SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material";
import { Layout } from "../../../../containers/layout/dashboard";
import { AddMigrationWrapper } from "./styled";
import { BaseButton } from "../../../../components/button/styled";
import { downloadMigrationMastersheetService } from "../../../../utils/apis/migration/downloadMastersheet";
import Cookies from "universal-cookie";
import { BaseFieldSet } from "../../../../components/formfields/fieldset/styled";
import { BaseLabel } from "../../../../components/formfields/label/styled";
import { BaseSelect } from "../../../../components/formfields/select/styled";
import { BaseOption } from "../../../../components/formfields/option/styled";
import { useQuery } from "@tanstack/react-query";
import { retrieveInstitutionsService } from "../../../../utils/apis/institution/retrieveInstitutions";
import { uploadMigrationMastersheetService } from "../../../../utils/apis/migration/uploadMastersheet";
import { BaseInput } from "../../../../components/formfields/input/styled";
import { BaseAlertModal } from "../../../../components/modals/alert";
import { BaseTable } from "../../../../components/table";
import { MiniSuccessTick } from "../../../../assets";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import { migrateTableService } from "../../../../utils/apis/migration/migrateTable";
import { finishMigrationService } from "../../../../utils/apis/migration/finishMigration";
import { useNavigate } from "react-router-dom";

export const AddMigration = () => {
	const headers = ["Sheet", "Status", "Action"];

	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();

	const [tabIndex, setTabIndex] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [isMigrating, setIsMigrating] = useState(false);
	const [activeTable, setActiveTable] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [isFinishMigrationError, setIsFinishMigrationError] = useState<
		string | null
	>(null);
	const [isDownloading, setIsDownloading] = useState(false);
	const [isFinishing, setIsFinishing] = useState(false);
	const [openFinishMigrationAlertModal, setOpenFinishMigrationAlertModal] =
		useState(false);
	const [migrationAlertModal, setMigrationAlertModal] = useState<{
		open: boolean;
		message: string | null;
		status: string | undefined;
	}>({
		open: false,
		message: null,
		status: undefined,
	});
	const [openValidationAlertModal, setOpenValidationAlertModal] =
		useState(false);
	const [migrationResult, setMigrationResult] = useState<
		Record<string, any>[] | null
	>(null);
	const [migrationStats, setMigrationStats] = useState<Record<
		string,
		any
	> | null>(null);
	const [formDetails, setFormDetails] = useState<Record<string, any>>({
		institution: " ",
		datasheet: null as File | null,
	});

	const { data: institutions } = useQuery({
		queryKey: ["institutions", TOKEN],
		queryFn: async () => retrieveInstitutionsService(TOKEN),
		enabled: !!TOKEN,
	});

	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>
	) => {
		const { name, value } = e.target;
		setFormDetails((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleFileChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const target = e.target as HTMLInputElement;
		const { name, files } = target;
		if (files && files[0]) {
			setFormDetails((prev) => ({
				...prev,
				[name]: files[0],
			}));
		}
	};

	const handleAlertModalPersist = () => {
		if (migrationAlertModal.open) {
			setMigrationAlertModal((prev) => ({
				...prev,
				open: false,
			}));
		}
		if (openValidationAlertModal) {
			setOpenValidationAlertModal(false);
		}
		if (openFinishMigrationAlertModal) {
			setOpenFinishMigrationAlertModal(false);
		}
		return setActiveTable(null);
	};

	const handleCloseAlertModal = () => {
		if (migrationAlertModal.open) {
			setMigrationAlertModal((prev) => ({
				...prev,
				open: false,
			}));
		}
		if (openValidationAlertModal) {
			setOpenValidationAlertModal(false);
		}
		if (openFinishMigrationAlertModal) {
			setOpenFinishMigrationAlertModal(false);
			navigate("/migration");
		}
		return setActiveTable(null);
	};

	const handleNavigateToPrevious = () => {
		setError(null);
		setIsFinishMigrationError(null);
		return setTabIndex(0);
	};

	const handleDownloadMasterSheet = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>
	) => {
		e.preventDefault();
		setIsDownloading(true);
		try {
			const blob: Blob = await downloadMigrationMastersheetService(TOKEN);
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = "migration_mastersheet.xlsx";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error: any) {
			console.error("Migration mastersheet download failed:", error);
		} finally {
			setIsDownloading(false);
		}
	};

	const handleMigrateTable = async (
		institutionId: string,
		tableName: string
	) => {
		setError(null);
		setIsMigrating(true);
		setMigrationAlertModal({
			open: false,
			message: null,
			status: undefined,
		});
		if (!tableName || !institutionId.trim()) {
			setIsMigrating(false);
			setError("No table selected for migration.");
			setMigrationAlertModal((prev) => ({
				...prev,
				open: true,
				status: "fail",
				message: `No table selected for migration.`,
			}));
			return;
		}
		try {
			const response = await migrateTableService(
				TOKEN,
				institutionId,
				tableName
			);
			if (response.status === "success") {
				setIsMigrating(false);
				setMigrationAlertModal((prev) => ({
					...prev,
					open: true,
					status: "success",
					message: `${tableName} table migrated successfully`,
				}));
			} else {
				setIsMigrating(false);
				setError(`Failed to migrate ${tableName} table. Please try again.`);
				setMigrationAlertModal((prev) => ({
					...prev,
					open: true,
					status: "fail",
					message: `Failed to migrate ${tableName} table`,
				}));
			}
		} catch (error: any) {
			setIsMigrating(false);
			setError(`Failed to migrate table. ${error.message}`);
			setMigrationAlertModal((prev) => ({
				...prev,
				open: true,
				status: "fail",
				message: `Failed to migrate ${tableName} table`,
			}));
			console.error("Failed to migrate table:", error);
		}
	};

	const handleFinishMigration = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>
	) => {
		e.preventDefault();
		if (!formDetails.institution) return;
		setIsFinishMigrationError(null);
		setIsFinishing(true);
		try {
			const response = await finishMigrationService(
				TOKEN,
				formDetails.institution
			);
			if (response.status === "success") {
				setIsFinishing(false);
				setOpenFinishMigrationAlertModal(true);
			} else {
				setIsFinishing(false);
				setIsFinishMigrationError(
					"Failed to finish migration. Please try again."
				);
			}
		} catch (error: any) {
			setIsFinishing(false);
			setIsFinishMigrationError(`Failed to finish migration. ${error.message}`);
			console.error("Failed to finish migration:", error);
		}
	};

	const handleTableCallToActionClick = async (
		e:
			| React.MouseEvent<HTMLButtonElement, MouseEvent>
			| React.MouseEvent<HTMLSpanElement, MouseEvent>,
		id: string,
		action?: string
	) => {
		setActiveTable(id);
		if (action === "view-details") {
			setOpenValidationAlertModal(true);
		}
		if (action === "migrate-table") {
			const institutionId = formDetails.institution;
			await handleMigrateTable(institutionId, id);
		}
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		setMigrationResult(null);
		setMigrationStats(null);
		const formData = new FormData();
		const data = {
			institution: formDetails.institution,
			datasheet: formDetails.datasheet,
		};
		Object.entries(data).forEach(([key, value]) => {
			formData.append(key, value);
		});
		try {
			const response = await uploadMigrationMastersheetService(formData, TOKEN);
			if (response.status === "success") {
				setIsLoading(false);
			} else {
				setIsLoading(false);
				setError(
					"Failed to upload migration form. Please check your credentials and try again."
				);
			}
			setTabIndex(1);
			if (response?.validation?.valid) {
				const results =
					(response.validation.tables || []).map((tableName: string) => ({
						table: tableName,
						status: "success",
					})) || [];
				setMigrationResult(results);
				setMigrationStats({
					totalTables: response?.validation?.tables?.length,
					successfulTables:
						response?.validation?.tables?.length -
						(response?.validation?.errors || [])?.length,
					problematicTables: response?.validation?.errors?.length,
				});
			} else {
				setMigrationResult(response?.validation?.errors || []);
				setMigrationStats({
					totalTables: response?.validation?.tables?.length,
					successfulTables:
						response?.validation?.tables?.length -
						(response?.validation?.errors || [])?.length,
					problematicTables: response?.validation?.errors?.length,
				});
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Failed to upload migration form. ${error.message}`);
			console.error("Failed to upload migration form:", error);
		}
	};

	const customBody = (
		<Stack>
			<Box>
				<Typography
					fontFamily={"Inter"}
					fontWeight={"400"}
					fontSize={20}
					lineHeight={"normal"}
					color={"#000000"}
					whiteSpace={"normal"}
					marginBlockEnd={"var(--basic-margin)"}
				>
					Errors for this table:
				</Typography>
			</Box>
			<Box
				component={"ul"}
				sx={{
					margin: 0,
					borderRadius: "8px",
					border: "1px solid #F39A9A",
					backgroundColor: "#FFF0F0",
					padding: "calc(var(--basic-padding)/4)",
				}}
			>
				{(() => {
					if (!migrationResult) return null;
					const tableGroup = migrationResult.find(
						(result) => result.table === activeTable
					);
					if (!tableGroup) {
						return <Typography>No errors for this table.</Typography>;
					}
					if (!tableGroup.errors || tableGroup.errors.length === 0) {
						return <Typography>No errors for this table.</Typography>;
					}
					return tableGroup.errors.map(
						(err: Record<string, any>, index: number) => (
							<Box key={index}>
								{err.type === "unrecognized_table" && (
									<Typography
										component={"li"}
										sx={{ listStylePosition: "inside" }}
									>
										{err.message}
									</Typography>
								)}
								{err.type === "missing_headers" && (
									<Typography
										component={"li"}
										sx={{ listStylePosition: "inside" }}
									>
										Missing headers: <strong>{err.missing?.join(", ")}</strong>
									</Typography>
								)}
								{err.type === "missing_required_value" && (
									<Typography
										component={"li"}
										sx={{ listStylePosition: "inside" }}
									>
										Row <strong>{err.row}</strong> is missing required field{" "}
										<strong>{err.column}</strong>
									</Typography>
								)}
								{err.type === "invalid_reference" && (
									<Typography
										component={"li"}
										sx={{ listStylePosition: "inside" }}
									>
										Row <strong>{err.row}</strong>: value{" "}
										<strong>{err.value}</strong> in{" "}
										<strong>{err.column}</strong> does not exist in reference
										table <strong>{err.reference.table}</strong> column{" "}
										<strong>{err.reference.column}</strong>
									</Typography>
								)}
								{err.type === "reference_table_missing" && (
									<Typography
										component={"li"}
										sx={{ listStylePosition: "inside" }}
									>
										{err.message}
									</Typography>
								)}
								{![
									"missing_headers",
									"missing_required_value",
									"invalid_reference",
									"unrecognized_table",
									"reference_table_missing",
								].includes(err.type) && (
									<Typography
										component={"li"}
										sx={{ listStylePosition: "inside" }}
									>
										Unknown error.
									</Typography>
								)}
							</Box>
						)
					);
				})()}
			</Box>
		</Stack>
	);

	return (
		<Layout id="migration">
			<AddMigrationWrapper>
				<BaseAlertModal
					title={`Validation Error(s): ${activeTable}`}
					open={openValidationAlertModal}
					handleClose={handleAlertModalPersist}
					hideCTA={true}
					hideTick={true}
					hideMessage={true}
					customBody={customBody}
					handleCallToAction={handleCloseAlertModal}
				/>
				<BaseAlertModal
					title={
						migrationAlertModal.status === "success" ? "Success" : "Failed"
					}
					errorTick={!(migrationAlertModal.status === "success")}
					message={migrationAlertModal.message ?? ""}
					open={migrationAlertModal.open}
					handleClose={handleAlertModalPersist}
					callToAction="Continue"
					handleCallToAction={handleCloseAlertModal}
				/>
				<BaseAlertModal
					title={"Success"}
					message={"Migration completed successfully"}
					open={openFinishMigrationAlertModal}
					handleClose={handleAlertModalPersist}
					callToAction="Continue"
					handleCallToAction={handleCloseAlertModal}
				/>
				<Stack
					overflow={"hidden"}
					direction={{ tablet: "row" }}
					gap={{
						mobile: "calc(var(--flex-gap)/2)",
						tablet: "var(--flex-gap)",
					}}
					alignItems={{ tablet: "center" }}
					justifyContent={"space-between"}
					marginBlockEnd={"var(--basic-margin)"}
				>
					<Box overflow={"hidden"}>
						<Typography
							variant="h1"
							fontFamily={"Inter"}
							fontWeight={700}
							fontSize={24}
							lineHeight={"normal"}
							color={"#373737"}
							marginBlockEnd={"calc(var(--basic-margin)/4)"}
						>
							Migrate Customer Data
						</Typography>
						{tabIndex === 1 && (
							<BaseButton
								variant="outlined"
								border="1px solid #D7D7D7"
								colour="#373737"
								bgcolor="#FFFFFF"
								padding="calc(var(--basic-padding)/4) calc(var(--basic-padding)/2)"
								fontSize={12}
								fontWeight={800}
								startIcon={<ArrowLeftIcon />}
								onClick={handleNavigateToPrevious}
							>
								<Typography
									variant={"button"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"inherit"}
									textTransform={"inherit"}
								>
									Back
								</Typography>
							</BaseButton>
						)}
					</Box>
					{tabIndex === 0 && (
						<Box overflow={"hidden"}>
							<BaseButton
								variant="contained"
								radius="7px"
								fontSize={16}
								fontWeight={800}
								bgcolor="#791377"
								padding="calc(var(--basic-padding)/3) calc(var(--basic-padding) * 1.5)"
								sx={{
									width: {
										mobile: "-webkit-fill-available",
										miniTablet: "auto",
									},
								}}
								onClick={handleDownloadMasterSheet}
							>
								{isDownloading ? (
									<CircularProgress color="inherit" className="loader" />
								) : (
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Download Mastersheet
									</Typography>
								)}
							</BaseButton>
						</Box>
					)}
				</Stack>
				{tabIndex === 0 && (
					<Fragment>
						<form onSubmit={handleSubmit}>
							<Grid2
								container
								component={"div"}
								spacing={"var(--flex-gap)"}
								className="fieldset-wrapper-I"
							>
								<Grid2 size={{ mobile: 12, laptop: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="#333333" fontWeight={500}>
											Institution
										</BaseLabel>
										<BaseSelect
											required
											fontWeight={400}
											name="institution"
											colour="#666666"
											bgcolor="#FFFFFF"
											value={formDetails.institution}
											onChange={(e) => handleChange(e)}
										>
											<BaseOption value={" "} colour="#666666" fontWeight={400}>
												Select institution
											</BaseOption>
											{institutions?.map(
												(institution: Record<any, any>, index: string) => {
													return (
														<BaseOption
															key={index}
															value={institution.id}
															colour="#666666"
															fontWeight={400}
														>
															{institution.name}
														</BaseOption>
													);
												}
											)}
										</BaseSelect>
									</BaseFieldSet>
								</Grid2>
							</Grid2>
							<BaseFieldSet>
								<BaseLabel colour="#333333" fontWeight={500}>
									Upload Master Sheet
								</BaseLabel>
								<Box component={"div"} className="file-box">
									<BaseInput
										as={"input"}
										type="file"
										name="datasheet"
										colour="#666666"
										fontWeight={400}
										inputProps={{ accept: "image/jpeg, image/png" }}
										onChange={(e) => handleFileChange(e)}
									/>
								</Box>
							</BaseFieldSet>
							{error && (
								<Typography
									fontFamily={"Inter"}
									fontWeight={"600"}
									fontSize={16}
									lineHeight={"normal"}
									color={"#FF0000"}
									whiteSpace={"normal"}
									marginBlockStart={"var(--basic-margin)"}
								>
									{error}
								</Typography>
							)}
							<Box component={"div"} className="button-box">
								<BaseButton
									type="submit"
									variant="contained"
									radius="7px"
									fontSize={16}
									fontWeight={800}
									bgcolor="#791377"
									padding="calc(var(--basic-padding)/3) calc(var(--basic-padding) * 1.5)"
								>
									{isLoading ? (
										<CircularProgress color="inherit" className="loader" />
									) : (
										<Typography
											variant={"button"}
											fontFamily={"inherit"}
											fontWeight={"inherit"}
											fontSize={"inherit"}
											lineHeight={"inherit"}
											color={"inherit"}
											textTransform={"inherit"}
										>
											Upload
										</Typography>
									)}
								</BaseButton>
							</Box>
						</form>
					</Fragment>
				)}
				{tabIndex === 1 && (
					<Stack
						gap={{
							mobile: "var(--flex-gap)",
						}}
					>
						<Box overflow={"hidden"}>
							<Typography
								variant="h3"
								fontFamily={"Inter"}
								fontWeight={500}
								fontSize={16}
								lineHeight={"normal"}
								color={"#000000"}
								marginBlockEnd={"calc(var(--basic-margin)/4)"}
							>
								Uploaded Master Sheet
							</Typography>
							<Box component={"div"} className="fieldset-wrapper-II">
								<Typography
									variant="h4"
									fontFamily={"Inter"}
									fontWeight={400}
									fontSize={14}
									lineHeight={"normal"}
									color={"#2C352D"}
									width={"100%"}
									display={"inline-flex"}
									alignItems={"center"}
									gap={"calc(var(--flex-gap)/4)"}
								>
									<Typography
										component={"span"}
										display={"inline-flex"}
										flexShrink={0}
									>
										<MiniSuccessTick />
									</Typography>{" "}
									<Typography
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"normal"}
										color={"inherit"}
									>
										File Uploaded:
									</Typography>
									<Typography
										fontFamily={"inherit"}
										fontWeight={700}
										fontSize={"inherit"}
										lineHeight={"normal"}
										color={"inherit"}
									>
										{formDetails.datasheet?.name || "N/A"}
									</Typography>
								</Typography>
							</Box>
						</Box>
						<Box overflow={"hidden"}>
							<Typography
								variant="h3"
								fontFamily={"Inter"}
								fontWeight={500}
								fontSize={16}
								lineHeight={"normal"}
								color={"#000000"}
								marginBlockEnd={"calc(var(--basic-margin)/4)"}
							>
								Validation Summary
							</Typography>
							<Stack
								direction={{ miniTablet: "row" }}
								gap={"calc(var(--flex-gap)/2)"}
							>
								<Box
									sx={{
										flex: 1,
										maxWidth: { miniTablet: "10rem" },
										borderRadius: "8px",
										overflow: "hidden",
										backgroundColor: "#DBE1E9",
										padding: "calc(var(--basic-padding)/2)",
									}}
								>
									<Typography
										variant="h4"
										fontFamily={"Inter"}
										fontWeight={800}
										fontSize={13}
										lineHeight={"normal"}
										color={"#3877D6"}
										marginBlockEnd={"calc(var(--basic-margin)/2)"}
									>
										Total Tables
									</Typography>
									<Typography
										variant="h4"
										fontFamily={"Inter"}
										fontWeight={800}
										fontSize={24}
										lineHeight={"normal"}
										color={"#3877D6"}
									>
										{migrationStats?.totalTables ?? 0}
									</Typography>
								</Box>
								<Box
									sx={{
										flex: 1,
										maxWidth: { miniTablet: "10rem" },
										borderRadius: "8px",
										overflow: "hidden",
										backgroundColor: "#E2EDE5",
										padding: "calc(var(--basic-padding)/2)",
									}}
								>
									<Typography
										variant="h4"
										fontFamily={"Inter"}
										fontWeight={800}
										fontSize={13}
										lineHeight={"normal"}
										color={"#32A34A"}
										marginBlockEnd={"calc(var(--basic-margin)/2)"}
									>
										Successful Tables
									</Typography>
									<Typography
										variant="h4"
										fontFamily={"Inter"}
										fontWeight={800}
										fontSize={24}
										lineHeight={"normal"}
										color={"#32A34A"}
									>
										{migrationStats?.successfulTables ?? 0}
									</Typography>
								</Box>
								<Box
									sx={{
										flex: 1,
										maxWidth: { miniTablet: "10rem" },
										borderRadius: "8px",
										overflow: "hidden",
										backgroundColor: "#F0DBDB",
										padding: "calc(var(--basic-padding)/2)",
									}}
								>
									<Typography
										variant="h4"
										fontFamily={"Inter"}
										fontWeight={800}
										fontSize={13}
										lineHeight={"normal"}
										color={"#D92525"}
										marginBlockEnd={"calc(var(--basic-margin)/2)"}
									>
										Tables with Errors
									</Typography>
									<Typography
										variant="h4"
										fontFamily={"Inter"}
										fontWeight={800}
										fontSize={24}
										lineHeight={"normal"}
										color={"#D92525"}
									>
										{migrationStats?.problematicTables ?? 0}
									</Typography>
								</Box>
							</Stack>
						</Box>
						<BaseTable
							headers={headers}
							activeRow={activeTable}
							isMigrating={isMigrating}
							rows={migrationResult ?? []}
							location="all-migration-results"
							handleItemClick={handleTableCallToActionClick}
						/>
						{isFinishMigrationError && (
							<Typography
								fontFamily={"Inter"}
								fontWeight={"600"}
								fontSize={16}
								lineHeight={"normal"}
								color={"#FF0000"}
								whiteSpace={"normal"}
							>
								{isFinishMigrationError}
							</Typography>
						)}
						{migrationStats?.totalTables ===
							migrationStats?.successfulTables && (
							<Box>
								<BaseButton
									variant="contained"
									radius="7px"
									fontSize={16}
									fontWeight={800}
									bgcolor="#791377"
									padding="calc(var(--basic-padding)/3) calc(var(--basic-padding) * 1.5)"
									onClick={handleFinishMigration}
								>
									{isFinishing ? (
										<CircularProgress color="inherit" className="loader" />
									) : (
										<Typography
											variant={"button"}
											fontFamily={"inherit"}
											fontWeight={"inherit"}
											fontSize={"inherit"}
											lineHeight={"inherit"}
											color={"inherit"}
											textTransform={"inherit"}
										>
											Finish
										</Typography>
									)}
								</BaseButton>
							</Box>
						)}
					</Stack>
				)}
			</AddMigrationWrapper>
		</Layout>
	);
};
